/*
 * Copyright (c) 2021 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import { cloneDeep } from 'lodash'
import { createTheme, emphasize, alpha } from '@material-ui/core/styles'
import '@fontsource/lato'
/*
 * Override default values for in the Material UI theme.
 * Defaults: https://material-ui.com/customization/default-theme/
 */

const defaultThemeOptions = {
  //default MUI spacing = 8px
  // 1rem = 16px
  palette: {
    // Start Cardigan Colors //
    bluePrimary: {
      main: '#1074C3',
      dark: '#3388CB',
      light: '#70ABDA',
    },
    blueSecondary: {
      main: '#0C4470',
      dark: '#305F85',
      light: '#6E8FA9',
    },
    notificationError: {
      main: '#C42014',
      dark: '#CC4137',
      light: '#DB7A73',
    },
    notificationWarning: {
      main: '#C67F00',
      dark: '#CD9839',
      light: '#D8BC89',
    },
    notificationSuccess: {
      main: '#128129',
      dark: '#359348',
      light: '#71B37F',
    },
    notificationInfo: {
      main: '#2739B1',
      dark: '#5160C0',
      light: '#7B86CE',
    },
    grayDark: {
      main: '#606877',
      dark: '#777E8B',
      light: '#A0A4AE',
    },
    grayRegular: {
      main: '#B0BAC5',
      dark: '#BBC4CD',
      light: '#CFD5DC',
    },
    grayLight: {
      main: '#CAD3DE',
      dark: '#D1D9E2',
      light: '#DEE4EA',
    },
    grayMid: {
      main: '#8D9AA8',
      dark: '#A8B1BA',
      light: '#C3C8CD',
    },
    textPrimary: {
      main: '#0D1A2D',
      dark: '#313C4C',
      light: '#6F7682',
    },
    textSecondary: {
      main: '#2B3545',
      dark: '#4A5360',
      light: '#808690',
    },
    textTertiary: {
      main: '#49515F',
      dark: '#646A76',
      light: '#92969F',
    },
    alertContainerFill: '#F6F6F6',
    inputHoverColor: '#F5F9FD',
    modalBottom: '#F3F5F7',
    modalHeader: 'rgba(192, 218, 239, 0.1)',
    modalGrayBorder: 'rgba(176, 186, 197, 0.45)',
    // End Cardigan Colors //
  },
  typography: {
    fontFamily: ['"Lato"', '"LatoFallback"', 'sans-serif'],
    fontSize: 14,
    fontWeightRegular: 400,
    fontWeightBold: 700,
    // Start Cardigan Font Styles //
    h1: {
      fontSize: '1.5rem', //24
      lineHeight: '1.2', //28.8
    },
    h2: {
      fontSize: '1.25rem', //20
      lineHeight: '1.2', //24
    },
    h3: {
      fontSize: '1rem', //16
      lineHeight: '1.25', //20
    },
    body1: {
      fontSize: '0.875rem', //14
      lineHeight: '1.2', //16.8
    },
    caption: {
      fontSize: '0.75rem', //12
      lineHeight: '1.2', //14.4
    },
    tiny: {
      fontSize: '0.625rem', //10
      lineHeight: '1.2', //12
    },
    // End Cardigan Font Styles //
    body2: {
      //footer links
      fontSize: '0.875rem', //14
      lineHeight: '1.7', //24
    },
    button: {
      fontSize: 14,
      letterSpacing: 0,
      lineHeight: '1.5',
    },
    muiTypeIcon: {
      fontSize: '1.25rem',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  overrides: {
    MuiGrid: {
      container: {
        '&[class*="spacing"]': {
          width: '100%',
        },
      },
    },
  },
}

/**
 * Builds RTL theme for Arabic
 * Currently used to increase font size by +1f
 * @returns {Object} RTL theme
 */
const createRTLTheme = () => {
  const rtlFontIncreaseValue = 1
  const rtlTheme = cloneDeep(defaultThemeOptions)

  const fontSizeList = ['body1', 'body2', 'h3', 'h4', 'h5', 'h6', 'button', 'muiTypeIcon']

  for (let i = 0; i < fontSizeList.length; i++) {
    if (typeof rtlTheme.typography[fontSizeList[i]].fontSize === 'string') {
      rtlTheme.typography[fontSizeList[i]].fontSize = `${
        parseInt(defaultThemeOptions.typography[fontSizeList[i]].fontSize, 10) + rtlFontIncreaseValue
      }px`
    } else {
      rtlTheme.typography[fontSizeList[i]].fontSize =
        defaultThemeOptions.typography[fontSizeList[i]].fontSize + rtlFontIncreaseValue
    }
  }
  rtlTheme.typography.fontSize = defaultThemeOptions.typography.fontSize + rtlFontIncreaseValue
  return rtlTheme
}

/**
 * Builds custom theme with institution colors.
 * @param {String} direction direction of theme based on language direction (ltr/rtl)
 * @returns {Object} theme
 */
const customizeTheme = (direction = 'ltr') => {
  const themeOptions = direction === 'ltr' ? defaultThemeOptions : createRTLTheme()
  const theme = createTheme({
    ...themeOptions,
    direction,
  })
  theme.overrides = {
    ...themeOptions.overrides,
    MuiPaper: {
      rounded: {
        borderRadius: '15px',
      },
    },
    MuiAlert: {
      outlinedError: {
        color: theme.palette.textPrimary.main,
        border: `1px solid ${theme.palette.notificationError.main}`,
        backgroundColor: theme.palette.alertContainerFill,
        borderLeft: '8px solid #c42014',
      },
      icon: {
        padding: 0,
      },
      message: {
        padding: 0,
      },
    },
    MuiButton: {
      root: {
        '&:disabled': {
          '&.MuiButton-outlined, &.MuiButton-contained': {
            color: '#49515F',
          },
        },
        '&.MuiButton-containedSecondary, &.MuiButton-containedSecondary .MuiButton-label > p, &.MuiButton-containedSecondary .MuiButton-label > div p':
          {
            fontWeight: 600,
          },
        '&.MuiButton-outlinedSecondary, &.MuiButton-outlinedSecondary .MuiButton-label > p, &.MuiButton-outlinedSecondary .MuiButton-label > div p':
          {
            fontWeight: 600,
          },
      },
      text: {
        '&:focus': {
          backgroundColor: alpha(theme.palette.bluePrimary.main, 0.12),
          outline: `${theme.palette.bluePrimary.main} auto`,
        },
      },
      label: {
        textTransform: 'none',
      },
      containedPrimary: {
        backgroundPosition: 'center',
        backgroundColor: theme.palette.bluePrimary.main,
        '&:hover': {
          backgroundColor: emphasize(theme.palette.bluePrimary.main, 0.08),
        },
        '&:focus': {
          backgroundColor: emphasize(theme.palette.bluePrimary.main, 0.24),
        },
        '&:active': {
          backgroundColor: theme.palette.bluePrimary.main,
        },
        '&:disabled': {
          backgroundColor: '#DEE4EA',
        },
      },
      containedSecondary: {
        '&:hover': {
          backgroundColor: emphasize(theme.palette.blueSecondary.main),
        },
        '&:focus': {
          backgroundColor: emphasize(theme.palette.blueSecondary.main, 0.2),
        },
        '&:active': {
          backgroundColor: theme.palette.blueSecondary.main,
        },
        '&:disabled': {
          backgroundColor: '#DEE4EA',
        },
      },
      outlinedPrimary: {
        '&:hover': {
          backgroundColor: emphasize(theme.palette.bluePrimary.main, 0.95),
        },
        '&:focus': {
          backgroundColor: emphasize(theme.palette.bluePrimary.main, 0.9),
        },
        '&:active': {
          backgroundColor: 'white',
        },
      },
      outlinedSecondary: {
        color: '#313C4C',
        borderRadius: '6px',
        '&:hover': {
          backgroundColor: alpha(theme.palette.blueSecondary.main, 0.04),
        },
        '&:focus': {
          backgroundColor: alpha(theme.palette.blueSecondary.main, 0.04),
        },
        '&:active': {
          backgroundColor: alpha(theme.palette.blueSecondary.main, 0.04),
        },
      },
      focusVisible: {
        '& .MuiTouchRipple-root > .MuiTouchRipple-ripplePulsate': {
          display: 'none',
        },
      },
    },
    MuiInputBase: {
      root: {
        height: 40,
        backgroundColor: '#FFF',
      },
    },
    MuiOutlinedInput: {
      notchedOutline: {
        borderColor: theme.palette.grayDark.main,
      },
    },
    MuiSvgIcon: {
      colorSecondary: {
        color: theme.palette.notificationError.main,
      },
    },
    MuiTypography: {
      root: {
        color: theme.palette.textPrimary.main,
      },
      colorSecondary: {
        color: theme.palette.notificationError.main,
      },
    },
  }
  theme.placeholderColor = '#BDBDBD'
  theme.breakpoints.mobile = () => theme.breakpoints.down('sm')
  theme.breakpoints.desktop = () => theme.breakpoints.up('md')
  return theme
}

export { defaultThemeOptions, customizeTheme }
